import React from 'react';
import './axiosConfig';
import { useState, useContext } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import AuthProvider, { AuthContext } from "./AuthContext";
import PrivateRoute from "./PrivateRoute";

import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import { AppToastProvider } from "./design-system/components/AppToastProvider";
import DemoModeBanner from "./components/DemoModeBanner";
import BillingAccessGate from "./components/BillingAccessGate";
import { getDemoRouteDestination } from "./demoMode";

import Login from "./scenes/login";
import AppOnly from "./scenes/app_only";
import Token_Registration from "./scenes/token_registration";
import Registration_Supervisor from "./scenes/registration_supervisor";
import Registration, { RegistrationSuccess } from "./scenes/registration";
import ForgetPassword from "./scenes/forget_password";
import ResetPassword from "./scenes/reset_password";
import Admin_Dashboard from "./scenes/admin_dashboard";
import SolverAdmin from "./scenes/solver_admin";
import Welcome from "./scenes/welcome";
import InitialSetup from "./scenes/initial_setup";
import QuickStart from "./scenes/quickstart";
import Team from "./scenes/team";
import EditUser from "./scenes/team/edit_user";
import UserFactSheet from "./scenes/team/user_fact_sheet_document";
import Availability from "./scenes/availability";
import AvailabilityTemplate from "./scenes/create_availability_template";
import ApprovalFlow from "./scenes/approval_flow";
import Settings from "./scenes/settings";
import Features from "./scenes/features";
import NewUser from "./scenes/new_user";
import PunchClock from "./scenes/punch_clock";
import ReportingEntry from "./scenes/reporting";
import PunchClockReporting from "./scenes/reporting/punch_clock";
import ShiftplanReporting from "./scenes/reporting/shiftplan";
import ReportExpense from "./scenes/report_expense";
import ReportInvoices from "./scenes/report_invoices";
import BillingOverview from "./scenes/billing_overview";
import LaborCostsPage from "./scenes/labor_costs";
import Line from "./scenes/line";
import FAQ from "./scenes/faq";
import Demand from "./scenes/demand";
import Planning from "./scenes/planning";
import Projects from "./scenes/projects";
import PlanningTemplate from "./scenes/create_planning_template";
import FeatureSetup from "./scenes/feature_setup";
import DesignSystemPage from "./design-system/scenes/design_system";
import Shiftplan from "./scenes/shiftplan_v2";
import Shiftplan_Overview from "./scenes/shiftplan_overview";
import EmployeeRoutes from "./scenes/employee/EmployeeRoutes";
import { isEmployeeAccessLevel } from "./utils/accessLevels";

const GatedShiftplan = () => (
  <BillingAccessGate featureKey="page.shiftplan">
    <Shiftplan />
  </BillingAccessGate>
);

function App() {
  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);

  return (
    <AuthProvider>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* clearOnRouteChange bewusst aus: An etlichen Stellen wird direkt
              nach der Erfolgsmeldung navigiert — der Toast soll den Wechsel
              ueberleben und nicht ungelesen verschwinden. */}
          <AppToastProvider clearOnRouteChange={false}>
          <div className="app">
            <AppContent isSidebar={isSidebar} setIsSidebar={setIsSidebar} />
          </div>
          </AppToastProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </AuthProvider>
  );
}

function AppContent({ isSidebar, setIsSidebar }) {
  const { user, selectedLocationId } = useContext(AuthContext);
  const isAuthenticated = !!user;
  // Mitarbeitende bekommen eine eigene, abschliessende Routenliste (EmployeeRoutes).
  const isEmployee = isAuthenticated && isEmployeeAccessLevel(user?.access_level);
  const location = useLocation();

  // Routes where the sidebar should be hidden
  const HIDE_SIDEBAR = new Set([
    "/welcome",
    "/initial-setup",
  ]);

  const showSidebar = isAuthenticated && isSidebar && !HIDE_SIDEBAR.has(location.pathname);
  const showTopbar  = isAuthenticated && !HIDE_SIDEBAR.has(location.pathname);
  const demoRouteDestination = getDemoRouteDestination(location.pathname);


  return (
    <>
      {showSidebar && <Sidebar />}
      <main className="content" style={{ minWidth: 0, minHeight: 0, flex: 1 }}>
        <div className="app-content-chrome">
          {showTopbar && <Topbar />}
          {showTopbar && <DemoModeBanner />}
        </div>
        <div className="app-route-content">
          {demoRouteDestination ? (
            <Navigate to={demoRouteDestination} replace />
          ) : isEmployee ? (
            <EmployeeRoutes key={selectedLocationId || "default-location"} />
          ) : (
          <Routes key={selectedLocationId || "default-location"}>
          <Route path="/forget_password" element={<ForgetPassword />} />
          <Route path="/reset_password" element={<ResetPassword />} />
          <Route path="/token_registration" element={<Token_Registration />} />
          <Route path="/registration/admin" element={<Registration_Supervisor />} />
          <Route path="/registration" element={<Registration />} />
          <Route path="/registration/success" element={<RegistrationSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/app-only" element={<AppOnly />} />
          <Route path="/payment" element={<Navigate to="/features" replace />} />
          <Route path="/welcome" element={<PrivateRoute component={Welcome} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/initial-setup" element={<PrivateRoute component={InitialSetup} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/quickstart" element={<PrivateRoute component={QuickStart} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/admin/dashboard" element={<PrivateRoute component={Admin_Dashboard} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/change/password" element={<Navigate to="/settings" replace />} />
          <Route path="/punch_clock" element={<PrivateRoute component={PunchClock} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report" element={<PrivateRoute component={ReportingEntry} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report/punch-clock" element={<PrivateRoute component={PunchClockReporting} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report/shiftplan" element={<PrivateRoute component={ShiftplanReporting} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report/expense" element={<PrivateRoute component={ReportExpense} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report/invoices" element={<PrivateRoute component={ReportInvoices} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report/billing" element={<PrivateRoute component={BillingOverview} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/report/labor-costs" element={<PrivateRoute component={LaborCostsPage} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/availability" element={<PrivateRoute component={Availability} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/availability/v2" element={<Navigate to="/availability" replace />} />
          <Route path="/create/availability/template" element={<PrivateRoute component={AvailabilityTemplate} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/team" element={<PrivateRoute component={Team} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/team/:id/edit" element={<EditUser />} />
          <Route path="/team/:id/fact-sheet" element={<UserFactSheet />} />
          <Route path="/approval/flow" element={<PrivateRoute component={ApprovalFlow} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/Update" element={<Navigate to="/settings" replace />} />
          <Route path="/Invite" element={<Navigate to="/team" replace />} />
          <Route path="/settings" element={<PrivateRoute component={Settings} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/company" element={<Navigate to="/settings" replace />} />
          <Route path="/new_user" element={<PrivateRoute component={NewUser} accessLevels={["Super_Admin"]} />} />
          <Route path="/feature/setup" element={<PrivateRoute component={FeatureSetup} accessLevels={["Super_Admin"]} />} />
          <Route path="/admin/solver" element={<PrivateRoute component={SolverAdmin} accessLevels={["Super_Admin"]} />} />
          <Route path="/design-system" element={<PrivateRoute component={DesignSystemPage} accessLevels={["Super_Admin"]} />} />
          <Route path="/Line" element={<PrivateRoute component={Line} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/features" element={<PrivateRoute component={Features} accessLevels={["Super_Admin"]} />} />
          <Route path="/FAQ" element={<PrivateRoute component={FAQ} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/demand" element={<PrivateRoute component={Demand} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/Planning" element={<PrivateRoute component={Planning} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/projects" element={<PrivateRoute component={Projects} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/create/planning/template" element={<PrivateRoute component={PlanningTemplate} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/shiftplan" element={<PrivateRoute component={GatedShiftplan} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/shiftplan/overview" element={<PrivateRoute component={Shiftplan_Overview} accessLevels={["Super_Admin", "Admin", "company_admin"]} />} />
          <Route path="/confirmation" element={<Navigate to="/features" replace />} />

          {/* Entry point and unknown paths */}
          <Route path="/" element={<Navigate to={isAuthenticated ? "/welcome" : "/login"} replace />} />
          <Route path="*" element={<Navigate to={isAuthenticated ? "/welcome" : "/login"} replace />} />
          </Routes>
          )}
        </div>
      </main>
    </>
  );
}

export default App;
